import MalKort from '../components/MalKort';
import React, { useState, useEffect } from 'react';
import { Clock, SubNav, Metric, KpiRow, DagligForsaljning, DagligSnabb, PlaneringTab, YoYUtforskare, VisaFlerRad, FjolRad, shiftISO } from '../components/SharedComponents';
import { tkr, fmtMonth } from '../utils';
import { getTopProducts, getWeeklyData, getDailyUnits, getDailyHours } from '../qlik';

const KNALLEN_COLOR = '#6B4E2E';
const TICKET_NAMES  = new Set(['Enkel', 'T&R', 'Enkel (5-12år)', 'T&R (5-12år)']);
// tkr för normala belopp, hela kr under 1 000 (annars visas "0 tkr")
const fmtBelopp = (v) => v >= 1000 ? tkr(v) : `${Math.round(v)} kr`;
const PERIOD_LABELS = [
  { key: 'today',       label: 'Idag'      },
  { key: 'yesterday',   label: 'Igår'      },
  { key: 'week',        label: 'Vecka'     },
  { key: 'month',       label: 'Månad'     },
  { key: 'threemonths', label: '3 månader' },
];

const btnStyle = (active) => ({
  background: active ? KNALLEN_COLOR : 'transparent',
  color: active ? '#fff' : 'var(--text-secondary)',
  border: '1px solid var(--border)', borderRadius: 20,
  padding: '3px 10px', fontSize: 11, cursor: 'pointer',
  fontFamily: 'system-ui,sans-serif', transition: 'all 0.15s',
});

function KnallenTopProdukt() {
  const [period,   setPeriod]   = useState('today');
  const [category, setCategory] = useState('alla');   // 'alla' | 'biljetter'
  const [sortBy,   setSortBy]   = useState('intakt'); // 'intakt' | 'antal'
  const [products, setProducts] = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [visaAlla, setVisaAlla] = useState(false);

  useEffect(() => {
    getTopProducts()
      .then(p  => setProducts(p))
      .catch(e => console.error('KnallenProducts error:', e))
      .finally(() => setLoading(false));
  }, []);

  const raw      = products?.knallen?.[period] || [];
  const filtered = category === 'biljetter'
    ? raw.filter(i =>  TICKET_NAMES.has(i.name))
    : raw.filter(i => !TICKET_NAMES.has(i.name));
  const full     = [...filtered].sort((a, b) => b[sortBy] - a[sortBy]);
  const items    = visaAlla ? full : full.slice(0, 7);
  const maxVal   = Math.max(...items.map(i => i[sortBy] || 0), 1);

  const valLabel = (item) =>
    sortBy === 'antal' ? `${item.antal} st` : `${Math.round(item.intakt).toLocaleString('sv-SE')} kr`;

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div className="card-title" style={{ marginBottom: 0 }}>Populäraste produkter</div>
        <div style={{ display: 'flex', gap: 4 }}>
          {PERIOD_LABELS.map(({ key, label }) => (
            <button key={key} onClick={() => setPeriod(key)} style={btnStyle(period === key)}>{label}</button>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
        <button onClick={() => setCategory('alla')}      style={btnStyle(category === 'alla')}>Knallen</button>
        <button onClick={() => setCategory('biljetter')} style={btnStyle(category === 'biljetter')}>Bandvagn</button>
        <div style={{ flex: 1 }} />
        <button onClick={() => setSortBy('intakt')} style={btnStyle(sortBy === 'intakt')}>Intäkt</button>
        <button onClick={() => setSortBy('antal')}  style={btnStyle(sortBy === 'antal')}>Antal</button>
      </div>
      {loading && <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Laddar...</div>}
      {!loading && items.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Ingen data för perioden</div>
      )}
      {!loading && items.map((item, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
          <div style={{ width: 14, fontSize: 10, color: '#bbb', textAlign: 'right', fontFamily: 'system-ui,sans-serif', flexShrink: 0 }}>{i + 1}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
              <span style={{ fontSize: 11, color: 'var(--text)', fontFamily: 'system-ui,sans-serif', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text)', fontFamily: 'system-ui,sans-serif', flexShrink: 0, marginLeft: 8 }}>{valLabel(item)}</span>
            </div>
            <div style={{ height: 3, background: 'var(--panel2)', borderRadius: 2 }}>
              <div style={{ height: '100%', width: `${Math.round(item[sortBy] / maxVal * 100)}%`, background: KNALLEN_COLOR, borderRadius: 2 }} />
            </div>
          </div>
        </div>
      ))}
      {!loading && <VisaFlerRad total={full.length} expanded={visaAlla} onToggle={() => setVisaAlla(v => !v)} />}
      <div className="note" style={{ marginTop: 10 }}>Källa: Qlik · Zettle-transaktioner Knallen</div>
    </div>
  );
}

export default function KnallenBlock({ data }) {
  const [sub, setSub] = useState('daglig');
  const [intaktVy, setIntaktVy] = useState('vecka'); // 'manad' | 'vecka'
  const [veckor, setVeckor] = useState(null);        // veckorader (för Vecka-vyn + YoY)
  useEffect(() => { getWeeklyData().then(setVeckor).catch(() => setVeckor([])); }, []);
  // Period & trend-tabellens upplösning + dagsdata (laddas först vid behov)
  const [tabellGran, setTabellGran] = useState('dag'); // 'manad' | 'vecka' | 'dag'
  const [units, setUnits] = useState(null);
  const [hoursD, setHoursD] = useState(null);
  useEffect(() => {
    if (tabellGran === 'dag' && units === null) {
      getDailyUnits().then(setUnits).catch(() => setUnits([]));
      getDailyHours().then(setHoursD).catch(() => setHoursD({ order: [], byDatum: {} }));
    }
  }, [tabellGran, units]);
  const rows  = data?.monthly || [];
  const daily = data?.daily   || [];
  const ytd   = data?.ytd     || {};

  const TABS = [
    { id: 'daglig', label: 'Daglig översikt' },
    { id: 'period', label: 'Period & trend'  },
    { id: 'planering', label: 'För planering' },
  ];

  // Senaste månad med faktisk intäkt
  const activeRow = rows.find(r => (r[3] || 0) > 0) || rows[0];
  const r0Rev    = activeRow?.[3]  || 0;
  const r0TB1    = activeRow?.[38] || 0;
  const r0Tim    = activeRow?.[19] || 0;
  const r0Pers   = activeRow?.[39] || 0;
  const r0TG     = r0Rev  > 0 ? Math.round(r0TB1 / r0Rev * 100)  : 0;
  const r0OmsH   = r0Tim  > 0 ? Math.round(r0Rev / r0Tim)        : 0;
  const r0KrPers = r0Pers > 0 ? Math.round(r0Rev / r0Pers)       : 0;
  const r0Label  = activeRow?.[0] ? fmtMonth(activeRow[0])        : '';
  const r0Lon    = Math.abs(activeRow?.[46] || 0);
  const r0PersPct = r0Rev > 0 && r0Lon > 0 ? Math.round(r0Lon / r0Rev * 100) : null;

  // Samma månad ifjol (ÅrMånad − 100) för KPI-jämförelserna
  const fRow0  = rows.find(x => Number(x[0]) === Number(activeRow?.[0]) - 100);
  const f0Rev  = fRow0?.[3]  || 0;
  const f0Tim  = fRow0?.[19] || 0;
  const f0Pers = fRow0?.[39] || 0;
  const f0Lon  = Math.abs(fRow0?.[46] || 0);
  const f0OmsH = f0Tim > 0 ? Math.round(f0Rev / f0Tim) : 0;
  const f0PersPct = f0Rev > 0 && f0Lon > 0 ? Math.round(f0Lon / f0Rev * 100) : null;

  // YTD
  const ytdRev    = ytd.knallen        || 0;
  const ytdTB1    = ytd.tb1Knallen     || 0;
  const ytdTim    = ytd.timKnallen     || 0;
  const ytdPers   = ytd.resenarKnallen || 0;
  const ytdTG     = ytdRev  > 0 ? Math.round(ytdTB1 / ytdRev * 100) : 0;
  const ytdOmsH   = ytdTim  > 0 ? Math.round(ytdRev / ytdTim)       : 0;
  const ytdKrPers = ytdPers > 0 ? Math.round(ytdRev / ytdPers)      : 0;
  const ytdLon    = Math.abs(ytd.lonKnallen || 0);
  const ytdPersPct = ytdRev > 0 && ytdLon > 0 ? Math.round(ytdLon / ytdRev * 100) : null;

  return (
    <div className="screen">
      <div className="header header-knallen">
        <div>
          <div className="header-title">Kariknallen + Bandvagn</div>
          <div className="header-name">Försäljning & trafik</div>
        </div>
        <Clock />
      </div>
      <SubNav tabs={TABS} active={sub} onChange={setSub} />
      {sub === 'daglig' && <MalKort enheter={['Knallen']} />}

      {/* ── DAGLIG ÖVERSIKT ── */}
      {sub === 'daglig' && (
        <>
          <DagligSnabb daily={daily} enhet="knallen" />
          <div className="grid-4">
            <Metric
              label={r0Label ? `Intäkt ${r0Label}` : 'Intäkt senaste månaden'}
              value={r0Rev > 0 ? tkr(r0Rev) : '–'}
              sub={<>
                {r0TB1 !== 0 ? `TB1 ${tkr(r0TB1)} · TG ${r0TG}%` : ''}
                <FjolRad cur={r0Rev} fjol={f0Rev} />
              </>}
              trend={r0TB1 > 0 ? 'up' : ''}
            />
            <Metric
              label={r0Label ? `Bandvagn ${r0Label}` : 'Bandvagn senaste månaden'}
              value={r0Pers > 0 ? `${r0Pers.toLocaleString('sv-SE')} pers` : '–'}
              sub={<>
                {r0KrPers > 0 ? `Snitt ${r0KrPers} kr/pers` : ''}
                <FjolRad cur={r0Pers} fjol={f0Pers} suffix="pers" />
              </>}
            />
            <Metric
              label="OMS/H"
              value={r0OmsH > 0 ? `${r0OmsH.toLocaleString('sv-SE')} kr/h` : '–'}
              sub={<>
                {r0Tim > 0 ? `${Math.round(r0Tim)} tim` : ''}
                <FjolRad cur={r0OmsH} fjol={f0OmsH} suffix="kr/h" />
              </>}
            />
            <Metric
              label={r0Label ? `Personal% ${r0Label}` : 'Personal%'}
              value={r0PersPct !== null ? `${r0PersPct}%` : '–'}
              sub={<>
                {r0Lon > 0 ? `Lön ${tkr(r0Lon)}` : 'Lön ÷ intäkt'}
                {f0PersPct !== null && <div style={{ marginTop: 2, fontSize: 11.5, fontFamily: 'system-ui,sans-serif' }}>Ifjol {f0PersPct}%</div>}
              </>}
              trend={r0PersPct === null ? '' : r0PersPct <= 35 ? 'up' : r0PersPct >= 55 ? 'down' : ''}
            />
          </div>
          <div className="grid-2">
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div className="card-title" style={{ marginBottom: 0 }}>{intaktVy === 'manad' ? 'Månadsintäkt Knallen' : 'Veckointäkt Knallen'}</div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button onClick={() => setIntaktVy('manad')} style={btnStyle(intaktVy === 'manad')}>Månad</button>
                  <button onClick={() => setIntaktVy('vecka')} style={btnStyle(intaktVy === 'vecka')}>Vecka</button>
                </div>
              </div>
              {intaktVy === 'manad' && rows.slice(0, 6).map((r, i) => {
                const rev  = r[3]  || 0;
                const pers = r[39] || 0;
                const fRev = rows.find(x => Number(x[0]) === Number(r[0]) - 100)?.[3] || 0;
                return (
                  <KpiRow
                    key={i}
                    label={fmtMonth(r[0])}
                    value={rev > 0 ? fmtBelopp(rev) : '–'}
                    sub={pers > 0 ? `${pers.toLocaleString('sv-SE')} resenärer` : null}
                    valueSub={<FjolRad cur={rev} fjol={fRev} fmt={fmtBelopp} />}
                    badge={i === 0 ? { type: 'blue', text: 'Senaste' } : null}
                  />
                );
              })}
              {intaktVy === 'vecka' && veckor === null && (
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Laddar veckodata...</div>
              )}
              {intaktVy === 'vecka' && veckor !== null && (veckor.filter(w => (w[17] || 0) > 0).slice(0, 8)).map((w, i) => {
                const rev  = w[17] || 0;
                const pers = w[24] || 0;
                // Samma vecka ifjol (ÅrVecka − 100, t.ex. 202629 → 202529)
                const fRev = (veckor.find(x => x[0] === w[0] - 100) || [])[17] || 0;
                return (
                  <KpiRow
                    key={i}
                    label={`V${w[0] % 100} ${Math.floor(w[0] / 100)}`}
                    value={rev > 0 ? fmtBelopp(rev) : '–'}
                    sub={pers > 0 ? `${pers.toLocaleString('sv-SE')} resenärer` : null}
                    valueSub={<FjolRad cur={rev} fjol={fRev} fmt={fmtBelopp} />}
                    badge={i === 0 ? { type: 'blue', text: 'Senaste' } : null}
                  />
                );
              })}
              <div className="note">Källa: Qlik — Resultatenhet Knallen · ifjol = samma {intaktVy === 'manad' ? 'månad' : 'ISO-vecka'} föregående år</div>
            </div>
            <KnallenTopProdukt />
          </div>
          <DagligForsaljning daily={daily} enhet="knallen" />
        </>
      )}

      {/* ── PERIOD & TREND ── */}
      {sub === 'period' && (
        <>
          <div className="grid-4">
            <Metric
              label="Knallen YTD"
              value={ytdRev > 0 ? tkr(ytdRev) : '–'}
              sub={ytdTB1 !== 0 ? `TB1 ${tkr(ytdTB1)} · TG ${ytdTG}%` : ''}
              trend={ytdTB1 > 0 ? 'up' : ''}
            />
            <Metric
              label="Bandvagn YTD"
              value={ytdPers > 0 ? `${ytdPers.toLocaleString('sv-SE')} pers` : '–'}
              sub={ytdKrPers > 0 ? `Snitt ${ytdKrPers} kr/pers` : ''}
            />
            <Metric
              label="OMS/H YTD"
              value={ytdOmsH > 0 ? `${ytdOmsH.toLocaleString('sv-SE')} kr/h` : '–'}
              sub={ytdTim > 0 ? `${Math.round(ytdTim)} tim` : ''}
            />
            <Metric
              label="Personal% YTD"
              value={ytdPersPct !== null ? `${ytdPersPct}%` : '–'}
              sub={ytdLon > 0 ? `Lön ${tkr(ytdLon)}` : 'Lön ÷ intäkt'}
              trend={ytdPersPct === null ? '' : ytdPersPct <= 35 ? 'up' : ytdPersPct >= 55 ? 'down' : ''}
            />
          </div>
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
              <div className="card-title" style={{ marginBottom: 0 }}>
                {tabellGran === 'manad' ? 'Månadsöversikt Knallen' : tabellGran === 'vecka' ? 'Veckoöversikt Knallen' : 'Dagsöversikt Knallen — senaste 14 dagarna'}
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                <button onClick={() => setTabellGran('manad')} style={btnStyle(tabellGran === 'manad')}>Månad</button>
                <button onClick={() => setTabellGran('vecka')} style={btnStyle(tabellGran === 'vecka')}>Vecka</button>
                <button onClick={() => setTabellGran('dag')}   style={btnStyle(tabellGran === 'dag')}>Dag</button>
              </div>
            </div>
            <div className="period-table">
              <div className="period-header period-row-8">
                <span>{tabellGran === 'manad' ? 'Månad' : tabellGran === 'vecka' ? 'Vecka' : 'Datum'}</span>
                <span>Intäkt</span>
                <span>TB1</span>
                <span>TG%</span>
                <span>Tim</span>
                <span>OMS/H</span>
                <span>Pers%</span>
                <span>Resenärer</span>
              </div>
              {(() => {
                // Enhetlig radmodell per upplösning. pctMin = intäktströskel för
                // att visa TG%/Pers% (annars exploderar procenten i uppstart).
                let rader = [];
                if (tabellGran === 'manad') {
                  rader = rows.map((r) => ({
                    label: fmtMonth(r[0]),
                    rev: r[3] || 0,
                    fRev: rows.find(x => Number(x[0]) === Number(r[0]) - 100)?.[3] || 0,
                    tb1: r[38] || 0, tim: r[19] || 0, pers: r[39] || 0,
                    lon: Math.abs(r[46] || 0), pctMin: 50000,
                  }));
                } else if (tabellGran === 'vecka') {
                  rader = (veckor || [])
                    .filter(w => (w[17] || 0) !== 0 || (w[13] || 0) > 0)
                    .slice(0, 16)
                    .map((w) => ({
                      label: `V${w[0] % 100} ${Math.floor(w[0] / 100)}`,
                      rev: w[17] || 0,
                      fRev: (veckor.find(x => x[0] === w[0] - 100) || [])[17] || 0,
                      tb1: w[26] || 0, tim: w[13] || 0, pers: w[24] || 0,
                      lon: Math.abs(w[25] || 0), pctMin: 15000,
                    }));
                } else {
                  // Dag: daily_units (intäkt/kostnad/lön, full historik → ifjol −364)
                  // + daily_hours (timmar). Resenärer finns inte på dagsnivå.
                  const byDatum = {};
                  for (const u of (units || [])) if (u.enhet === 'Knallen') byDatum[u.datum] = u;
                  const hIdx = hoursD ? hoursD.order.indexOf('Knallen') : -1;
                  const p2 = (n) => String(n).padStart(2, '0');
                  const t = new Date();
                  const todayISO = `${t.getFullYear()}-${p2(t.getMonth() + 1)}-${p2(t.getDate())}`;
                  const WD = ['Sön', 'Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör'];
                  for (let i = 0; i < 14; i++) {
                    const d = shiftISO(todayISO, -i);
                    const u = byDatum[d];
                    const f = byDatum[shiftISO(d, -364)];
                    const dt = new Date(d + 'T12:00:00Z');
                    rader.push({
                      label: `${WD[dt.getUTCDay()]} ${dt.getUTCDate()}/${dt.getUTCMonth() + 1}`,
                      rev: u?.intakt || 0,
                      fRev: f?.intakt || 0,
                      tb1: (u?.intakt || 0) + (u?.kostnad || 0),
                      tim: hIdx >= 0 ? ((hoursD.byDatum[d] || [])[hIdx] || 0) : 0,
                      pers: null,
                      lon: Math.abs(u?.lon || 0), pctMin: 2000,
                    });
                  }
                }
                const fmtRev = tabellGran === 'dag'
                  ? (v) => `${Math.round(v).toLocaleString('sv-SE')} kr`
                  : tkr;
                if (tabellGran === 'dag' && units === null) {
                  return <div style={{ fontSize: 12, color: 'var(--text-secondary)', padding: '10px 0', fontFamily: 'system-ui,sans-serif' }}>Laddar dagsdata...</div>;
                }
                return rader.map((r, i) => {
                  const pctOk = r.rev >= r.pctMin;
                  const tg = r.rev > 0 ? Math.round(r.tb1 / r.rev * 100) : 0;
                  const omsh = r.tim > 0 ? Math.round(r.rev / r.tim) : 0;
                  const perspct = r.rev > 0 ? Math.round(r.lon / r.rev * 100) : 0;
                  return (
                    <div key={i} className="period-row period-row-8">
                      <span>{r.label}</span>
                      <span>
                        {r.rev > 0 ? fmtRev(r.rev) : '–'}
                        {r.fRev > 0 && (
                          <div style={{ fontSize: 10.5, color: 'var(--text-secondary)' }}>
                            ifjol {fmtRev(r.fRev)}{r.rev > 0 && <span style={{ fontWeight: 600, color: r.rev >= r.fRev ? '#3B6D11' : '#A32D2D' }}> · {r.rev >= r.fRev ? '+' : ''}{Math.round((r.rev - r.fRev) / r.fRev * 100)}%</span>}
                          </div>
                        )}
                      </span>
                      <span>{r.tb1 !== 0 ? fmtRev(r.tb1) : '–'}</span>
                      <span>{pctOk ? `${tg}%` : '–'}</span>
                      <span>{r.tim > 0 ? Math.round(r.tim).toLocaleString('sv-SE') : '–'}</span>
                      <span>{omsh > 0 ? `${omsh.toLocaleString('sv-SE')} kr` : '–'}</span>
                      <span>{pctOk && r.lon > 0 ? `${perspct}%` : '–'}</span>
                      <span>{r.pers > 0 ? r.pers.toLocaleString('sv-SE') : '–'}</span>
                    </div>
                  );
                });
              })()}
            </div>
            <div className="note">
              Källa: Qlik — Resultatenhet Knallen · ifjol = {tabellGran === 'manad' ? 'samma kalendermånad' : tabellGran === 'vecka' ? 'samma ISO-vecka' : 'samma veckodag (−364 dgr)'} föregående år{tabellGran === 'dag' ? ' · resenärer finns ej på dagsnivå' : ''}
            </div>
          </div>

          {/* YoY-utforskaren — gräv i utvecklingen mot ifjol */}
          <YoYUtforskare enheter={['Knallen']} hoursEnhet="Knallen" color={KNALLEN_COLOR} />
        </>
      )}

      {/* ── FÖR PLANERING ── */}
      {sub === 'planering' && (
        <PlaneringTab enheter={['Knallen']} color="#6B4E2E" />
      )}
    </div>
  );
}
